import React, { useEffect } from 'react';
import {
    Trophy,
    RefreshCcw,
    ShieldCheck,
    Users,
    Wallet,
    Target,
    Layers,
    FileText,
    Mic,
    Video,
    Eye,
    LayoutDashboard,
    Flame,
    FolderOpen,
    Wrench,
    Package
} from 'lucide-react';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{n}</div>
        <div>
            <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{h}</h3>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function PranayStrategy() {

    useEffect(() => {
        document.title = "Pranay Jham | Brand Research";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Pranay Jham</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Research</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Built from your questionnaire · 4 Sep 2026
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The research<br />behind your brand.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        Your positioning, your story spine, your content series and the system that runs it.<br className="hidden sm:block" /> Everything below comes from your answers and our call. Nothing is templated.
                    </p>
                </section>

                {/* POSITIONING */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The positioning</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The founder who went<br />zero to one, twice.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Everyone teaches the highlight reel. You teach the unglamorous math of a bootstrapped D2C, and your ACTIVeat story is proof nobody in your space can copy.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Trophy} heading="1 Cr in year one" subheading="bootstrapped, profitable from day 1 · under 10% marketing spend, no aggregator crutch" accent="indigo" />
                        <InfoCard icon={RefreshCcw} heading="The COVID zero" subheading="5 months shut, profits wiped, zero blue-collar layoffs · then 0 to 1 all over again" accent="indigo" />
                        <InfoCard icon={ShieldCheck} heading="Repeatable, not lucky" subheading="40 to 50% customer retention and a second brand · liftoff as a system, which is exactly what your audience is buying" accent="indigo" />
                    </div>
                </section>

                {/* AUDIENCE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The audience read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Who this brand is for.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Users} heading="The growth crowd" subheading="24 to 40, job or startup experience, starting or just started their D2C · confused on pricing, placement, distribution · they engage the hardest" accent="rose" />
                        <InfoCard icon={Wallet} heading="The money slice" subheading="founders already running a D2C with revenue and stuck · same content reaches them, and they are the ones who later pay for your time" accent="rose" />
                        <InfoCard icon={Target} heading="Your page starts fresh" subheading="most of your 960 followers are personal connections, your own estimate · we build from zero and still sign the guarantee" accent="rose" />
                    </div>
                </section>

                {/* STORY SPINE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The story spine</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Three chapters<br />every video draws from.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The Lean Year" s="Zero to 1 Cr in 12 months, bootstrapped, under 10% marketing, no Swiggy or Zomato dependence. This chapter is the playbook." />
                        <NumRow n="02" h="The Zero" s="COVID shuts the business for 5 months, profits gone, every blue-collar salary still paid, restart from scratch. This chapter is the character. It buys the trust everything else stands on." />
                        <NumRow n="03" h="The Rebuild" s="Zero to one again, then brand two, this time with systems. This chapter is the proof it repeats, which is exactly what your audience wants to believe about themselves." />
                    </div>
                </section>

                {/* CONTENT SERIES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The content series</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four series.<br />No more guessing.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Every bucket you listed maps into one of these. Each video is one narrow problem, solved completely, in your direct voice.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Flame} heading="Uncomfortable Truths" subheading="your phrase, now a series · the Rs 99 course that ends at 10K, what 10x ROAS agencies leave out · polarizing takes, fact-checked by us before they ship" accent="amber" />
                        <InfoCard icon={FolderOpen} heading="The 0 to 1 Files" subheading="your own numbers as episodes · 1 Cr with under 10% marketing, the 5 months at zero, the restart · first-person story learnings" accent="amber" />
                        <InfoCard icon={Wrench} heading="Lean Machine" subheading="staying lean while bootstrapping · cost hacks, website CRO, research tools, Meta Ads breakdowns" accent="amber" />
                        <InfoCard icon={Package} heading="First 100 Orders" subheading="for the founder with a product and no plan · pricing, placement, distribution channels, understanding your market" accent="amber" />
                    </div>
                </section>

                {/* LEAD MAGNET + FUNNEL */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The funnel</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From a comment<br />to your corner.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You chose free resource plus comment as the CTA. This is that engine.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The First 100 Orders Playbook" s="A free resource that fully solves your audience's first problem: how to price, place and sell a new D2C product without burning on ads. Good enough that we could charge for it. That is the point." />
                        <NumRow n="02" h="Comment to unlock" s="Videos end with one word to comment. Comments are the metric that proves someone watched and wants more." />
                        <NumRow n="03" h="The DM automation delivers" s="Our Meta-verified system sends the playbook, nurtures the conversation and flags the serious ones. DM automation is also one of your content buckets, so the engine doubles as proof." />
                        <NumRow n="04" h="The next problem is yours" s="Whoever finishes the playbook now has a running start and a bigger question: how do I scale this? That question is your future consulting business." />
                    </div>
                </section>

                {/* THE SYSTEM / SOP */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The system</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Your week,<br />start to ship.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Built around what you told us: 1 to 2 shorts a day, home, car or office setup, quick approvals, and zero AI in the scripts.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Mic} heading="You drop voice notes" subheading="a thought hits, you open WhatsApp · we also ask the right questions to pull the take out of you" accent="emerald" />
                        <InfoCard icon={FileText} heading="We script in your voice" subheading="hooks, structure, words · teleprompter-ready, built from your experience, never from generic AI output" accent="emerald" />
                        <InfoCard icon={Video} heading="You record 15 minutes a day" subheading="your setup, one or two takes · the camera presence stays the unfair advantage" accent="emerald" />
                        <InfoCard icon={Layers} heading="We edit with one identity" subheading="your raw style kept, the look unified · text hooks, subtitles and covers as one system" accent="emerald" />
                        <InfoCard icon={Eye} heading="You approve, we ship" subheading="minimum one reel a day on Instagram, YouTube Shorts and Facebook · bio, story flow and carousels optimized as the audience grows" accent="emerald" />
                        <InfoCard icon={LayoutDashboard} heading="The data runs the loop" subheading="every video tracked for shares, saves and comments, the metrics you picked · what works gets doubled, what fails teaches" accent="emerald" />
                    </div>
                </section>

                {/* 90 DAYS + GUARANTEE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The 90 days</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The roadmap<br />and the guarantee.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mb-10">
                        <NumRow n="W1" h="Onboarding" s="Your arc downloaded, positioning and pillars locked, first scripts written. The heaviest back and forth happens here, on purpose." />
                        <NumRow n="W2-4" h="Format testing" s="Daily posting starts. Series formats and hooks tested against real data, not opinion." />
                        <NumRow n="W5-8" h="Double down" s="What pulled gets volume. The playbook and DM engine go live once the audience is real." />
                        <NumRow n="W9-12" h="Compound" s="Winning formats scale, carousels and story flow layer in, and the 10,000 target lands." />
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The guarantee</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                10,000 in-ICP followers in 90 days, people starting or running D2C businesses, not vanity numbers. Written into a legally bound contract. If we miss it, you get 100% of your money back.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        We lock the number on our call.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        The research is done, the system is ready, and week 1 starts the day we sign.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
